<template>
  <InfoCard title="Session">
    <div v-if="!loading" class="info__grid scrollbar">
      <div v-if="user" class="info__grid-field created-by-row">
        <div class="description">Created By</div>
        <div class="value">
          <UserIcon
            :user="user"
            :size="'32px'"
          />
        </div>
        <span class="ml-2">{{ user ? user.first_name + ' ' + user.last_name : '' }}</span>
      </div>
      <div v-if="currentValidateSession" class="info__grid-field session-id-row">
        <div class="description">Session ID</div>
        <EncodedIdField
          :id="currentValidateSession.id"
          :type="'validator'"
          :hideLabel="true"
        />
      </div>
      <div v-if="currentValidateSession && !currentValidateSessionStatus.toLowerCase().includes('complete')" class="info__grid-field current-stage-row">
        <div class="description">Current Stage</div>
        <TaskStatusBars
          :task="currentValidateSession.docker_task"
          :singleBar="true"
          class="progress-bar"
        />
      </div>
      <div v-if="currentValidateSessionStatus" class="info__grid-field status-row">
        <div class="description">Status</div>
        <div>
          <span v-if="currentValidateSessionStatus.toLowerCase().includes('complete')" class="status-tag _complete"><div class="status-icon">&#x2713;</div>Complete</span>
          <span v-else-if="currentValidateSessionStatus.toLowerCase().includes('error')" class="status-tag _error"><div class="status-icon">&#x2716;</div>Error</span>
          <span v-else-if="currentValidateSessionStatus.toLowerCase().includes('terminate')" class="status-tag _warning"><div class="status-icon">&#x21;</div>Terminated</span>
          <span v-else-if="currentValidateSessionStatus" class="status-tag _running"><div class="running-icon" />
            {{ String(currentValidateSessionStatus).charAt(0).toUpperCase() + String(currentValidateSessionStatus).slice(1) }}
          </span>
        </div>
      </div>
      <div v-if="currentValidateSession && currentValidateSession.docker_task" class="info__grid-field name-row">
        <div class="description">Name</div>
        <div
          v-tooltip="currentValidateSession.docker_task.name ? currentValidateSession.docker_task.name : 'Unknown'"
          class="value"
        >
          {{ currentValidateSession.docker_task.name ? currentValidateSession.docker_task.name : "Unknown" }}
        </div>
      </div>
      <div v-if="currentValidateSession" class="info__grid-field model-row">
        <div class="description">Model</div>
        <div
          v-tooltip="currentValidateSession.params.validate_params ? currentValidateSession.params.validate_params.model : 'Unknown'"
          class="value"
        >
          {{ currentValidateSession.params.validate_params ? currentValidateSession.params.validate_params.model : 'Unknown' }}
        </div>
      </div>
      <div v-if="currentTrainingSession" class="info__grid-field training-session-row">
        <div class="description">Training Session</div>
        <div
          v-tooltip="currentTrainingSession.name ? `View details for ${currentTrainingSession.name}` : 'Unknown'"
          class="value hyperlink"
          @click="$emit('navigate-to-training-session', currentTrainingSession.id)"
        >
          {{ currentTrainingSession.name ? currentTrainingSession.name : 'Unknown' }}
        </div>
      </div>
      <div v-if="dataset" class="info__grid-field dataset-row">
        <div class="description">Dataset</div>
        <div
          v-tooltip="dataset.name ? `Open gallery for dataset ${dataset.name}` : 'Unknown'"
          class="value hyperlink"
          @click="$emit('to-gallery')"
        >
          {{ dataset.name ? dataset.name : 'Unknown' }}
        </div>
      </div>
      <div v-if="dataset" class="info__grid-field dataset-id-row">
        <div class="description">Dataset ID</div>
        <div class="value">
          <EncodedIdField
            :id="dataset.id"
            :type="'dataset'"
            :hideLabel="true"
          />
        </div>
      </div>
      <div v-if="currentValidateSession && currentValidateSession.params.gt_annotation_set_id" class="info__grid-field annotation-set-id-row">
        <div class="description">Annotation Set ID</div>
        <div class="value">
          <EncodedIdField
            :id="currentValidateSession.params.gt_annotation_set_id"
            :type="'annotation set'"
            :hideLabel="true"
          />
        </div>
      </div>
      <div v-if="currentValidateSession" class="info__grid-field start-date-row">
        <div class="description">Start Date</div>
        <div class="value"><span v-if="currentValidateSession.docker_task">{{ formatTime(currentValidateSession.docker_task.date) }}</span></div>
      </div>
      <div v-if="currentValidateSession" class="info__grid-field duration-row">
        <div class="description">Duration</div>
        <div class="value">
          <span v-if="currentValidateSession.docker_task && getEndTime(currentValidateSession.docker_task)" class="duration">
            {{ getEndTime(currentValidateSession.docker_task) }}
          </span>
        </div>
      </div>
    </div>
    <div v-else id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </InfoCard>
</template>

<script setup>
import InfoCard from '@/components/InfoCard.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import UserIcon from '@/components/UserIcon.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';

defineProps({
  currentValidateSession: { type: Object, default: null },
  currentTrainingSession: { type: Object, default: null },
  currentValidateSessionStatus: { type: String, default: '' },
  dataset: { type: Object, default: null },
  user: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

defineEmits(['navigate-to-training-session', 'to-gallery']);

const getEndTime = (docker_task) => {
  if (docker_task.end_date !== "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = new Date(docker_task.end_date) - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else if (docker_task.end_date === "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = Date.now() - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else {
    return null;
  }
};

const formatTime = (str) => `${new Date(str).toLocaleTimeString('default', {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
})}`;
</script>

<style lang="scss" scoped>
.info__grid-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 5px;
}

.info__grid-field + .info__grid-field {
  margin-top: 10px;
}

.info__grid {
  display: grid;
  overflow-y: auto;

  .description {
    font-size: 0.95rem;
    font-weight: 600;
    height: 32px;
    color: var(--body-text-color);
    width: 250px;
  }
  .value {
    font-size: 0.95rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .progress-bar {
    max-width: 400px;
    min-width: 100px;
  }
}

.hyperlink {
  cursor: pointer;
  color: var(--color-primary-500);
  &:hover {
    text-decoration: underline;
  }
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-width: 100px;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
  border: 2px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  @include themify() {
    color: themed('color-black');
  }

  &._complete {
    @include themify() {
      color: themed('color-status-complete');
      background: themed('color-status-complete-background');
    }
  }
  &._running {
    @include themify() {
      color: themed('color-status-running');
      background: themed('color-status-running-background');
    }
  }
  &._error {
    @include themify() {
      color: themed('color-status-error');
      background: themed('color-status-error-background');
    }
  }
  &._warning {
    @include themify() {
      color: themed('color-status-warning');
      background: themed('color-status-warning-background');
    }
  }

  .running-icon {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }

  .status-icon {
    color: inherit;
    margin-right: 4px;
  }
}
</style>
